import Vue from 'vue';
import { Floor, FloorEdit } from '@/interfaces/locations';
import { Commit } from 'vuex';

interface FloorState {
	floors: {
		[id: number]: Floor;
	};
	selectedFloor: Floor | null;
}

export default {
	namespaced: true,
	state: {
		floors: [],
		selectedFloor: null,
	} as FloorState,
	getters: {
		getFloors: (state: FloorState): Floor[] => {
			return Object.values(state.floors);
		},

		getSiteFloors:
			(state: FloorState) =>
			(fkSite: number): Floor[] => {
				return Object.values(state.floors).filter((floor: Floor) => floor.fk_site === fkSite);
			},

		getFloor:
			(state: FloorState) =>
			(id: number): Floor | undefined => {
				return state.floors[id];
			},

		getSelectedFloor: (state: FloorState): Floor | null => {
			return state.selectedFloor;
		},
	},
	mutations: {
		SET_FLOORS(state: FloorState, floors: Floor[]): void {
			const ret: { [id: number]: Floor } = {};
			state.floors = floors.reduce((obj, floor) => {
				obj[floor.id] = floor;
				return obj;
			}, ret);
		},

		ADD_FLOOR(state: FloorState, floor: Floor): void {
			Vue.set(state.floors, floor.id, floor);
		},

		CHANGE_FLOOR(state: FloorState, floor: Floor & { id: number }): void {
			const oldFloor = state.floors[floor.id] || {};
			Vue.set(state.floors, floor.id, { ...oldFloor, ...floor });
		},

		DELETE_FLOOR(state: FloorState, id: number): void {
			Vue.delete(state.floors, id);
		},

		CHANGE_SELECTED_FLOOR(state: FloorState, floor: Floor): void {
			state.selectedFloor = floor;
		},

		CLEAN_FLOOR(state: FloorState): void {
			state.floors = {};
		},

		CLEAN_SELECTED_FLOOR(state: FloorState): void {
			state.selectedFloor = null;
		},
	},
	actions: {
		async fetchFloors({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/areas`);

			if (response && response.status === 200) {
				commit('SET_FLOORS', response.data.data);
			}
		},

		async addFloor(
			{ commit }: { commit: Commit },
			{ payload }: { payload: FloorEdit }
		): Promise<void> {
			const response = await Vue.axios.post(`${process.env.VUE_APP_API_URL}/areas`, payload);
			if (response && response.status === 201) {
				commit('ADD_FLOOR', response.data.data);
			}
		},

		async modifyFloor(
			{ commit }: { commit: Commit },
			{ id, payload }: { id: number; payload: FloorEdit }
		): Promise<void> {
			const response = await Vue.axios.put(`${process.env.VUE_APP_API_URL}/areas/${id}`, payload);
			if (response && response.status === 200) {
				commit('CHANGE_FLOOR', response.data.data);
			}
		},

		async deleteFloor({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(`${process.env.VUE_APP_API_URL}/areas/${id}`);
			if (response && response.status === 200) {
				commit('DELETE_FLOOR', id);
			}
		},

		selectFloor({ commit }: { commit: Commit }, { floor }: { floor: Floor }): void {
			if (floor) {
				commit('CHANGE_SELECTED_FLOOR', floor);
			}
		},

		cleanFloor({ commit }: { commit: Commit }): void {
			commit('CLEAN_FLOOR');
		},

		cleanSelectedFloor({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_FLOOR');
		},

		cleanAllFloor({ commit }: { commit: Commit }): void {
			commit('CLEAN_FLOOR');
			commit('CLEAN_SELECTED_FLOOR');
		},
	},
};
